import {
  Box,
  Button,
  Typography,
  SwipeableDrawer,
  Stack,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { FullScreenLoader } from "../components/FullScreenLoader";
import CapitalMovementModal from "../components/modals/CapitalMovementModal";
import BotStatsContainer from "./BotStatsContainer";
import { useResponsive } from "../hooks/useResponsive";
import { useAuth } from "../hooks/useAuth";
import { DemoBotSelectionProvider, useDemoBotSelection } from "../hooks/useDemoBotSelection";
import { BotSelectionModal } from "../components/modals/BotSelectionModal";
import ReportsView from "./ReportsView";
import TradingView from "./TradingView";
import { ReconnectingWebSocket } from "../socket";
import type { MainSection } from "../components/header/headerDesktop";
import HeaderMobile from "../components/header/headerMobile";
import { BotNavList } from "../components/navigation/BotNavList";
import { SectionNav } from "../components/navigation/SectionNav";
import { UserMenu } from "../components/navigation/UserMenu";
import { LiveIndicator } from "../components/navigation/LiveIndicator";
import { MobileBottomNav, BOTTOM_NAV_CLEARANCE } from "../components/navigation/MobileBottomNav";
import { PageHeader } from "../components/ui/PageHeader";
import { AmbientBackground } from "../components/ui/AmbientBackground";
import { ChainAvatar, chainName, chainOf, pairOf } from "../components/ui/ChainAvatar";
import { isWaitingForFunds } from "../utils/botUtils";
import { botState, gasSeverity } from "../utils/botMetrics";
import { tokens } from "../theme";

import { DashboardData } from '../hooks/types';

interface DashboardProps {
  socket: ReconnectingWebSocket;
  dashboardData: DashboardData;
}

const SIDEBAR_WIDTH = { md: 256, lg: 268 };
const MOBILE_TOPBAR_HEIGHT = 60;

/** "1 bot", "14 bots"; "1 strategy", "8 strategies". */
const countLabel = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const Dashboard = ({ socket, dashboardData }: DashboardProps) => {
  return (
    <DemoBotSelectionProvider availableBots={dashboardData.allBots.map(b => b.stats.botName)}>
      <DashboardContent socket={socket} dashboardData={dashboardData} />
    </DemoBotSelectionProvider>
  );
}

const DashboardContent = ({ socket, dashboardData }: DashboardProps) => {
  const { isMobile, isTouch } = useResponsive();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { signOut, groups, user } = useAuth();
  const isAdmin = groups?.includes("admin");
  const isDemo = groups?.includes("DEMO");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Navigation lives in the URL (?s=section&f=family&t=tab): back/forward,
  // reload and links keep the user's place.
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('s');
  const currentSection: MainSection =
    isAdmin && (sectionParam === 'reports' || sectionParam === 'trading') ? sectionParam : 'lpp';
  const familyParam = searchParams.get('f');

  const { openBotSelection, selectedBots: demoSelectedBots } = useDemoBotSelection();

  const handleSendReport = async (introText: string, recipients: string[], groupName?: string, startDate?: Date, endDate?: Date, reportType?: string) => {
      try {
          const res = await fetch('/api/reports/send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ introText, recipients, groupName, startDate, endDate, reportType })
          });
          if (res.ok) {
              const body = await res.json().catch(() => ({}));
              const skipped: string[] = Array.isArray(body?.invalidRecipients) ? body.invalidRecipients : [];
              setSnackbarMessage(
                skipped.length > 0
                  ? `Report generation started · skipped ${skipped.length} invalid address${skipped.length === 1 ? '' : 'es'}: ${skipped.join(', ')}`
                  : 'Report generation started'
              );
              setSnackbarSeverity(skipped.length > 0 ? 'warning' : 'success');
              setSnackbarOpen(true);
          } else if (res.status === 409) {
              setSnackbarMessage('A report is already being generated, try again when it finishes');
              setSnackbarSeverity('warning');
              setSnackbarOpen(true);
          } else {
              setSnackbarMessage('Error starting report generation');
              setSnackbarSeverity('error');
              setSnackbarOpen(true);
          }
      } catch (e) {
          setSnackbarMessage('Error contacting server');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
      }
  };

  useEffect(() => {
    const isJustLoggedIn = sessionStorage.getItem('isJustLoggedIn');
    if (isDemo && isJustLoggedIn) {
      openBotSelection();
      sessionStorage.removeItem('isJustLoggedIn');
    }
  }, [isDemo, openBotSelection]);

  const {
    data,
    loading,
    error,
    selectedBot,
    setSelectedBot,
    isModalOpen,
    setIsModalOpen,
    botsToDisplay,
    onDeleteMovement,
    onUpdateMovement,
    handleDataRefresh,
    allBots,
    groupedBotsByChain,
    botMap,
    botTokens,
    buybacks,
    tradingActivityBuybacks,
    groupedBots,
  } = dashboardData;

  const activeNetwork =
    selectedBot === null ? "ALL" : selectedBot.split("_")[0];

  const filteredGroupedBotsByChain = React.useMemo(() => {
    if (isDemo) {
      const result: Record<string, string[]> = {};
      for (const chain in groupedBotsByChain) {
        const families = groupedBotsByChain[chain].filter(family => {
            const familyBots = groupedBots[family] || [];
            return familyBots.some(botId => demoSelectedBots.includes(botId));
        });
        if (families.length > 0) {
          result[chain] = families;
        }
      }
      return result;
    }
    return groupedBotsByChain;
  }, [isDemo, demoSelectedBots, groupedBotsByChain, groupedBots]);

  const filteredBotsToDisplay = React.useMemo(() => {
    if (isDemo) {
        return botsToDisplay.filter(bot => demoSelectedBots.includes(bot.stats.botName));
    }
    return botsToDisplay;
  }, [isDemo, demoSelectedBots, botsToDisplay]);

  // Filter buybacks by user group
  const filteredBuybacks = React.useMemo(() => {
    if (!buybacks) return buybacks;
    if (isAdmin) return buybacks; // ADMIN sees all
    if (isDemo) {
      const bySelected = (b: any) => demoSelectedBots.includes(b.botId);
      return {
        completed: buybacks.completed.filter(bySelected),
        pending: buybacks.pending.filter(bySelected),
      };
    }
    // Others: backend already filtered bots/buybacks via groups
    return buybacks;
  }, [buybacks, isAdmin, isDemo, demoSelectedBots]);

  const visibleBots = useMemo(
    () => (isDemo ? allBots.filter((b) => demoSelectedBots.includes(b.stats.botName)) : allBots),
    [isDemo, allBots, demoSelectedBots]
  );

  // Phones: the top bar slides away while scrolling down and returns on scroll up,
  // so the docked tabs + tab bar don't eat a quarter of the screen.
  const [headerHidden, setHeaderHidden] = useState(false);
  useEffect(() => {
    if (!isMobile) {
      setHeaderHidden(false);
      return;
    }
    let lastY = window.scrollY;
    let frame = 0;
    let suppressUntil = 0;
    const onProgrammatic = () => {
      suppressUntil = Date.now() + 600;
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        if (Date.now() < suppressUntil) {
          lastY = y;
          return;
        }
        const dy = y - lastY;
        if (Math.abs(dy) < 8) return;
        setHeaderHidden(dy > 0 && y > 140);
        lastY = y;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("app:programmatic-scroll", onProgrammatic);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("app:programmatic-scroll", onProgrammatic);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isMobile]);

  // Sidebar list: is there more below the visible part? (drives the bottom fade)
  const navScrollRef = React.useRef<HTMLDivElement>(null);
  const [navHasMore, setNavHasMore] = useState(false);
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const update = () => setNavHasMore(el.scrollHeight - el.scrollTop - el.clientHeight > 2);
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", update);
      ro?.disconnect();
    };
  }, [isMobile, currentSection, filteredGroupedBotsByChain]);

  // URL → selected family (ignored until the family exists in the data).
  useEffect(() => {
    const family = familyParam && !groupedBots[familyParam] ? familyParam.replace(/_\d+$/, "") : familyParam;
    const next = family && groupedBots[family] ? family : null;
    if (next !== selectedBot) setSelectedBot(next);
  }, [familyParam, groupedBots, selectedBot, setSelectedBot]);

  // Alert badge in the phone header, about what is on screen (the family, or everything on the overview):
  // bots out of range or without a position, low on gas, or awaiting funds. Red when any of them is
  // critical (out of range, no position, gas needed for a transaction), amber otherwise.
  const attention = useMemo((): { count: number; tone: "error" | "warning" } => {
    if (!isAdmin) return { count: 0, tone: "warning" };
    const scope = selectedBot
      ? visibleBots.filter((b) => b.stats.botName.replace(/_\d+$/, "") === selectedBot)
      : visibleBots;
    let count = 0;
    let critical = false;
    scope.forEach(({ stats }) => {
      const state = botState(stats);
      const noRange = state === "out-of-range" || state === "idle";
      const lowGas = stats.isLowGas === true;
      if (noRange || lowGas || isWaitingForFunds(stats)) count += 1;
      if (noRange || (lowGas && gasSeverity(stats) === "error")) critical = true;
    });
    return { count, tone: critical ? "error" : "warning" };
  }, [isAdmin, visibleBots, selectedBot]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          textAlign: "center",
          p: 3,
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    // Use the loading state from useDashboardData
    return <FullScreenLoader />;
  }

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "auto" });

  const selectFamily = (familyOrBot: string | null) => {
    const family = familyOrBot && !groupedBots[familyOrBot] ? familyOrBot.replace(/_\d+$/, "") : familyOrBot;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (family) next.set('f', family);
      else next.delete('f');
      next.delete('s');
      next.delete('t');
      return next;
    });
    setMobileOpen(false);
    scrollToTop();
  };

  const changeSection = (section: MainSection) => {
    // Tapping the active LPP tab again goes back to the overview.
    if (section === currentSection) {
      if (section === 'lpp' && selectedBot) selectFamily(null);
      else scrollToTop();
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (section === 'lpp') next.delete('s');
      else next.set('s', section);
      next.delete('t');
      return next;
    });
    setMobileOpen(false);
    scrollToTop();
  };

  const email: string | undefined = user?.signInDetails?.loginId;
  const showBottomNav = isMobile && isAdmin;
  const chains = Object.keys(filteredGroupedBotsByChain);
  const familyCount = chains.reduce((acc, c) => acc + filteredGroupedBotsByChain[c].length, 0);
  const fundFamilyCount = Object.values(groupedBotsByChain).reduce((acc, families) => acc + families.length, 0);
  // Bot tab open in a family: its full id is shown (and copyable) under the title — on touch there is no hover/right-click.
  const tabParam = searchParams.get('t');
  const openBotId =
    selectedBot && tabParam && tabParam !== 'SUMMARY' && (groupedBots[selectedBot] || []).includes(tabParam) ? tabParam : null;
  const copyBotId = (botId: string) => {
    navigator.clipboard?.writeText(botId);
    setSnackbarMessage(`Copied ${botId}`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const headerActions = (
    <>
      <LiveIndicator lastUpdate={data?.lastUpdate} />
      {isAdmin && (
        <Button variant="contained" color="secondary" startIcon={<AddRoundedIcon />} onClick={() => setIsModalOpen(true)}>
          Add movement
        </Button>
      )}
    </>
  );

  // Phones: live status under the title and a 44px "+" for admins (desktop actions are hidden there).
  const mobileMeta = <LiveIndicator inline lastUpdate={data?.lastUpdate} />;
  const mobileAction = isAdmin ? (
    <IconButton
      onClick={() => setIsModalOpen(true)}
      aria-label="Add movement"
      sx={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        color: "#111",
        background: tokens.goldGradient,
        boxShadow: tokens.goldGlow,
        "&:hover": { background: tokens.goldGradient },
      }}
    >
      <AddRoundedIcon />
    </IconButton>
  ) : undefined;

  const pageHeader =
    selectedBot === null ? (
      <PageHeader
        title="Overview"
        subtitle={
          isDemo
            ? // The hero and KPIs always cover the whole fund; the cards follow the selection.
              `Whole fund · showing ${familyCount} of ${countLabel(fundFamilyCount, "strategy", "strategies")}`
            : [
                countLabel(chains.length, "chain", "chains"),
                countLabel(familyCount, "strategy", "strategies"),
                countLabel(visibleBots.length, "bot", "bots"),
              ].join(" · ")
        }
        actions={headerActions}
        mobileMeta={mobileMeta}
        mobileAction={mobileAction}
      />
    ) : (
      <PageHeader
        leading={<ChainAvatar chain={chainOf(selectedBot)} size={isMobile ? 40 : 52} />}
        parent={isMobile ? undefined : { label: "Overview", onClick: () => selectFamily(null) }}
        title={pairOf(selectedBot)}
        subtitle={
          openBotId ? (
            <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, maxWidth: "100%" }}>
              <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {chainName(chainOf(selectedBot))} · {openBotId}
              </Box>
              <IconButton
                size="small"
                aria-label={`Copy bot id ${openBotId}`}
                onClick={() => copyBotId(openBotId)}
                sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}
              >
                <ContentCopyRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          ) : isMobile ? (
            `${chainName(chainOf(selectedBot))} · ${filteredBotsToDisplay.length} bot${filteredBotsToDisplay.length === 1 ? "" : "s"}`
          ) : (
            `${chainName(chainOf(selectedBot))} · ${selectedBot} · ${filteredBotsToDisplay.length} bot${filteredBotsToDisplay.length === 1 ? "" : "s"}`
          )
        }
        actions={headerActions}
        mobileMeta={mobileMeta}
        mobileAction={mobileAction}
      />
    );

  const navList = (touch: boolean, large = false) => (
    <BotNavList
      groupedBotsByChain={filteredGroupedBotsByChain}
      groupedBots={groupedBots}
      botsData={data?.botsData}
      selected={currentSection === 'lpp' ? selectedBot : undefined}
      onSelect={selectFamily}
      touch={touch}
      large={large}
    />
  );

  return (
    <Box sx={{ minHeight: "100dvh", position: "relative" }}>
      <AmbientBackground />
      <BotSelectionModal groupedBots={groupedBots} />
      <CapitalMovementModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={(msg?: string) => {
          handleDataRefresh();
          if (msg) {
            setSnackbarMessage(msg);
            setSnackbarOpen(true);
          }
        }}
        botMap={botMap}
        botTokens={botTokens}
        socket={socket}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "flex-start",
          gap: { md: 3, lg: 4 },
          pl: { md: 2.5 },
          pr: { md: 3, lg: 4 },
        }}
      >
        {/* Floating glass sidebar — tablet & desktop */}
        {!isMobile && (
          <Box component="nav" aria-label="Navigation" sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, position: "sticky", top: 0, py: 2.5 }}>
            <Box
              sx={{
                height: "calc(100dvh - 40px)",
                display: "flex",
                flexDirection: "column",
                borderRadius: "28px",
                background: tokens.glass.bg,
                border: `1px solid ${tokens.glass.border}`,
                boxShadow: `${tokens.glass.highlight}, ${tokens.glass.shadow}`,
                backdropFilter: tokens.glass.blur,
                WebkitBackdropFilter: tokens.glass.blur,
                overflow: "hidden",
              }}
            >
              <Box
                component="button"
                onClick={() => selectFamily(null)}
                aria-label="Go to overview"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 2.5,
                  pt: 2.75,
                  pb: 1.75,
                  border: 0,
                  background: "none",
                  cursor: "pointer",
                  color: "text.primary",
                  textAlign: "left",
                  "&:hover img": { opacity: 0.85 },
                }}
              >
                <img src="/logo.png" alt="Teks" height={24} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, lineHeight: 1.2 }} noWrap>
                    Teks Capital
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.35 }} noWrap>
                    Liquidity Monitor
                  </Typography>
                </Box>
              </Box>

              {/* Tight enough for iPad landscape (834px, 44px section rows on touch) to list every family unscrolled. */}
              <Box
                ref={navScrollRef}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: 1.5,
                  pb: 1.5,
                  // Fade the bottom edge only while more rows sit below it (never over a list that fits).
                  maskImage: navHasMore ? "linear-gradient(180deg, #000 92%, transparent)" : "none",
                }}
              >
                {isAdmin ? (
                  // Sections as rows; the strategy list nests under LPP and hides in Reports/Trading.
                  <SectionNav value={currentSection} onChange={changeSection}>
                    {navList(isTouch)}
                  </SectionNav>
                ) : (
                  navList(isTouch)
                )}
              </Box>

              <Box sx={{ p: 1.25, borderTop: `1px solid ${tokens.borderSubtle}` }}>
                <UserMenu
                  variant="card"
                  email={email}
                  groups={groups ?? []}
                  lastUpdate={data?.lastUpdate}
                  onSignOut={signOut}
                  onSelectBots={isDemo ? openBotSelection : undefined}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Main column */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isMobile && (
            <Box
              component="header"
              sx={{
                position: "sticky",
                top: 0,
                zIndex: (theme) => theme.zIndex.appBar,
                height: `calc(${MOBILE_TOPBAR_HEIGHT}px + env(safe-area-inset-top))`,
                pt: "env(safe-area-inset-top)",
                px: 2,
                display: "flex",
                alignItems: "center",
                bgcolor: "rgba(0,0,0,0.55)",
                backdropFilter: "blur(20px) saturate(160%)",
                WebkitBackdropFilter: "blur(20px) saturate(160%)",
                borderBottom: `1px solid ${tokens.borderSubtle}`,
                transform: headerHidden ? "translateY(-100%)" : "none",
                transition: "transform 220ms ease",
              }}
            >
              <HeaderMobile
                currentSection={currentSection}
                selectionLabel={selectedBot ? pairOf(selectedBot) : null}
                onOpenBots={() => setMobileOpen(true)}
                onLogoClick={() => selectFamily(null)}
                onBack={currentSection === 'lpp' && selectedBot ? () => selectFamily(null) : undefined}
                alertCount={attention.count}
                alertTone={attention.tone}
                lastUpdate={data?.lastUpdate}
                email={email}
                groups={groups ?? []}
                signOut={signOut}
                onSelectBots={isDemo ? openBotSelection : undefined}
              />
            </Box>
          )}

          <Box
            component="main"
            sx={{
              px: { xs: 2, sm: 3, md: 0 },
              pt: { xs: 1, md: 0 },
              pb: showBottomNav
                ? `calc(${BOTTOM_NAV_CLEARANCE}px + env(safe-area-inset-bottom) + 12px)`
                : { xs: 4, md: 6 },
              maxWidth: 1640,
              mx: "auto",
            }}
          >
            {currentSection === 'lpp' && (
              <>
                {pageHeader}
                {(selectedBot === null ? allBots.length > 0 : botsToDisplay.length > 0) ? (
                  <BotStatsContainer
                    bots={selectedBot === null ? allBots : filteredBotsToDisplay}
                    buybacksData={filteredBuybacks}
                    pendingBuybacksSummary={data?.pendingBuybacksSummary}
                    onDeleteMovement={onDeleteMovement}
                    onEditMovement={onUpdateMovement}
                    forceHomeTab={selectedBot === null}
                    onDoubleClickBot={(botId) => selectFamily(botId)}
                    onDoubleClickBack={() => selectFamily(null)}
                    activeNetwork={activeNetwork}
                    isAdmin={isAdmin}
                    socket={socket}
                    stickyTop={
                      isMobile
                        ? headerHidden
                          ? "calc(8px + env(safe-area-inset-top))"
                          : `calc(${MOBILE_TOPBAR_HEIGHT + 8}px + env(safe-area-inset-top))`
                        : 14
                    }
                    onFeedback={(msg, sev = 'success') => {
                      setSnackbarMessage(msg);
                      setSnackbarSeverity(sev);
                      setSnackbarOpen(true);
                    }}
                  />
                ) : (
                  <Typography color="text.secondary">Select a bot to see details.</Typography>
                )}
              </>
            )}

            {currentSection === 'reports' && isAdmin && (
              <Box sx={{ pt: { xs: 1, md: 3 } }}>
                <ReportsView
                  onBack={() => changeSection('lpp')}
                  onSendReport={handleSendReport}
                  socket={socket}
                />
              </Box>
            )}

            {currentSection === 'trading' && isAdmin && (
              <Box sx={{ pt: { xs: 1, md: 3 } }}>
                <TradingView
                  socket={socket}
                  botMap={botMap}
                  botTokens={botTokens}
                  buybacksData={filteredBuybacks}
                  tradingActivityBuybacksData={tradingActivityBuybacks}
                  pendingBuybacksSummary={data?.pendingBuybacksSummary}
                  onRefresh={handleDataRefresh}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Bot picker — bottom sheet on phones & tablets in portrait (drag down to close) */}
      {isMobile && (
        <SwipeableDrawer
          anchor="bottom"
          open={mobileOpen}
          onOpen={() => setMobileOpen(true)}
          onClose={() => setMobileOpen(false)}
          disableSwipeToOpen
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              maxHeight: "calc(100dvh - env(safe-area-inset-top) - 24px)",
              display: "flex",
              flexDirection: "column",
              pb: "env(safe-area-inset-bottom)",
              borderTopLeftRadius: 28,
              borderTopRightRadius: 28,
            },
          }}
        >
          <Box sx={{ pt: 1.25, display: "grid", placeItems: "center", flexShrink: 0 }} aria-hidden>
            <Box sx={{ width: 40, height: 5, borderRadius: 999, bgcolor: "rgba(255,255,255,0.22)" }} />
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ pl: 2.5, pr: 1.5, height: 56, flexShrink: 0 }}
          >
            <Typography sx={{ fontWeight: 750, fontSize: "1.125rem", letterSpacing: "-0.01em" }}>Strategies</Typography>
            <IconButton onClick={() => setMobileOpen(false)} aria-label="Close strategy picker" sx={{ width: 44, height: 44 }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <Box sx={{ flexGrow: 1, overflowY: "auto", px: 1.5, pb: 3 }}>{navList(true, true)}</Box>
        </SwipeableDrawer>
      )}

      {showBottomNav && <MobileBottomNav value={currentSection} onChange={changeSection} />}

      {/* Global feedback for movement success */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={showBottomNav ? { bottom: `calc(${BOTTOM_NAV_CLEARANCE}px + env(safe-area-inset-bottom)) !important` } : undefined}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
